import React, { useCallback, useEffect, useState } from 'react';
import styles from './UploadFontModal.module.scss';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import VideoService from '../../../../../service/VideoService';
import Icon from '../../../../../components/Icon/Icon';
import Modal from '../../../../../components/Modal';
import { ToastNotification } from '../../../../../components/Toast/Toast';
import Spinner from '../../../../../../common/components/Spinner';
import { CustomButton } from '../../../../../components/Button/Button';
import CloseButton from '../../../../../components/CloseButton/CloseButton';

const UploadFontModal = ({ isOpen, onClose, onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState([]);
  const [fontFamily, setFontFamily] = useState('');

  const { t } = useTranslation();

  const onDrop = useCallback(async ([acceptedFile]) => {
    console.log('onDrop', 'acceptedFile', acceptedFile);
    if (acceptedFile) {
      const fontFamilyFromFile = acceptedFile.path.split('.')[0];

      const URLObj = window.URL || webkitURL;
      const blobFileUrl = URLObj.createObjectURL(acceptedFile);

      let fontFile = await fetch(blobFileUrl).then(res => res.arrayBuffer());
      const fontFace = new FontFace(fontFamilyFromFile, fontFile);
      try {
        const loadedFont = await fontFace.load();
        document.fonts.add(loadedFont);
        setFontFamily(fontFamilyFromFile);
      } catch (e) {
        ToastNotification({
          message: `${t('company.settings.fonts.create.error')}`,
        });
        console.error('Font loading error:', e);
      }
      setFile(acceptedFile);
      return;
    }

    ToastNotification({
      message: `${t('clip_organizer.add_modal.wrong_file_format')} ${t('company.settings.fonts.create.available_format')}`,
    });
  }, []);

  useEffect(() => {
    const uploadFile = async fontFile => {
      try {
        // Get signed url to upload font file
        const url = await VideoService.uploadFont(fontFile);
        const { id } = await VideoService.createFont({ filePath: url });

        ToastNotification({
          message: t('company.settings.fonts.create.success'),
          iconName: 'success',
        });
        onUpload(id);
        onClose();
      } catch (err) {
        console.error('upload font error', err);
        ToastNotification({
          message: `${t(err.response.status === 409 ? 'company.settings.fonts.create.conflict' : 'company.settings.fonts.create.error', { name: fontFamily })}`,
        });
        setUploading(false);
        setFile([]);
        setFontFamily('');
      }
    };

    if (uploading) {
      uploadFile(file);
    }
  }, [file, uploading]);

  useEffect(() => {
    if (!isOpen) {
      setUploading(false);
      setFile([]);
      setFontFamily('');
    }
  }, [isOpen]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    onDrop,
    accept: { 'font/ttf': ['.ttf'] },
    disabled: uploading,
  });

  return (
    <Modal open={isOpen} onClose={onClose} className={styles.modal}>
      <div className={styles.modalContainer}>
        <div className={styles.headerModal}>
          <span className={styles.modalTitle}>{t('company.settings.fonts.create.upload_font')}</span>
          <CloseButton onClose={onClose} />
        </div>
        <div {...getRootProps()} className={styles.dropzoneWrapper}>
          <input {...getInputProps()} />
          {uploading ? (
            <div className={styles.explanationContainer}>
              <Spinner width="40px" height="40px" size="3px" className={styles.processingSpinner} />
            </div>
          ) : (
            <div className={styles.explanationContainer}>
              {fontFamily ? (
                <div className={styles.previewContainer}>
                  <span style={{ fontFamily: `"${fontFamily}"`, fontSize: '42px', color: '#333333' }}>{fontFamily}</span>
                </div>
              ) : (
                <>
                  <h3 className={styles.header}>{t('company.settings.logos.create.drag_and_drop.title')}</h3>
                  {!isDragActive && (
                    <CustomButton variant="secondary" className={styles.browseBtn}>
                      <Icon src="search" className={styles.searchIconContainer} />
                      <span>{t('company.settings.logos.create.drag_and_drop.button')}</span>
                    </CustomButton>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <CustomButton variant="secondary" label={t('general.cancel')} className={styles.footerBtn} onClick={onClose} />
          <CustomButton
            variant="primary"
            label={t('general.save')}
            className={styles.footerBtn}
            onClick={() => setUploading(true)}
            disabled={uploading || !fontFamily}
          />
        </div>
      </div>
    </Modal>
  );
};

export default UploadFontModal;
